/**
 * OpsRow — ряд операторов квеста/спецверсии (A31): силуэты «туалетных
 * табличек» м(cyan)/ж(pink)/любой(dashed). Тап — цикл м→ж→любой, long-press
 * 480мс — меню «Удалить оператора» (флаг long глушит следующий click),
 * «+» добавляет «любого». Хранение — строка crew_rule в алфавите
 * scoring.ts violatesGender: «М»/«Ж»/«Н» (crewToOps/opsToCrew).
 * OpsMini — мини-силуэты для мета-строки карточки.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import cfg from "./config.module.css";
import css from "./quests.module.css";

export type OpG = "m" | "f" | "a";

const FROM_CREW: Record<string, OpG> = { М: "m", Ж: "f", Н: "a", N: "a" };
const TO_CREW: Record<OpG, string> = { m: "М", f: "Ж", a: "Н" };

/** «ММЖН» → ['m','m','f','a']; null/мусор → пусто. */
export const crewToOps = (rule: string | null | undefined): OpG[] =>
  (rule ?? "").toUpperCase().split("").map((ch) => FROM_CREW[ch]).filter((g): g is OpG => !!g);

/** ['m','f'] → «МЖ»; пусто → "" (PUT снимет правило — NULL). */
export const opsToCrew = (ops: OpG[]): string => ops.map((g) => TO_CREW[g]).join("");

/**
 * Состав квеста для UI: crew_rule, а без правила — operators (int, headcount)
 * как N «любых» (A31: int остаётся живым для OO-читателей, состав derive).
 */
export const opsOfQuest = (q: { crew_rule?: string | null; operators?: number }): OpG[] => {
  const fromRule = crewToOps(q.crew_rule);
  return fromRule.length
    ? fromRule
    : Array.from({ length: Math.max(0, q.operators ?? 0) }, () => "a" as OpG);
};

const G_CLS: Record<OpG, string> = { m: css.oM ?? "", f: css.oF ?? "", a: css.oA ?? "" };
const G_TITLE: Record<OpG, string> = { m: "парень", f: "девушка", a: "любой" };

/** Силуэт (эталон opSvg js:79-85): m — брюки, f — юбка, a — dashed-контур. */
export function OpSvg({ g, w }: { g: OpG; w: number }) {
  const head = <circle cx="12" cy="4.6" r="2.6" />;
  if (g === "a") {
    return (
      <svg width={w} height={w} viewBox="0 0 24 24" fill="currentColor">
        {head}
        <path d="M9 8.4h6l1 12.6H8z" strokeDasharray="2 1.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    );
  }
  return (
    <svg width={w} height={w} viewBox="0 0 24 24" fill="currentColor">
      {head}
      {g === "m"
        ? <path d="M8.6 8.4h6.8l-.5 7h-1.6l-.5 6h-1.6l-.5-6h-1.6z" />
        : <path d="M9.6 8.4h4.8l2.2 8.2h-2.6l-.4 4.8h-3.2l-.4-4.8H7.4z" />}
    </svg>
  );
}

/** Мини-силуэты для qmeta (эталон ops-mini). */
export function OpsMini({ ops }: { ops: OpG[] }) {
  return (
    <span className={css.opsMini}>
      {ops.map((g, i) => (
        <span key={i} className={`${css.om} ${G_CLS[g]}`}><OpSvg g={g} w={12} /></span>
      ))}
    </span>
  );
}

const LP_MS = 480;

export function OpsRow({ ops, onChange }: { ops: OpG[]; onChange: (next: OpG[]) => void }) {
  /* Меню удаления: индекс оператора + rect якоря (fixed-позиционирование). */
  const [menu, setMenu] = useState<{ i: number; anchor: DOMRect } | null>(null);
  const [on, setOn] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const lpT = useRef(0);
  const longRef = useRef(false);

  /* Появление 120мс + тап мимо закрывает (capture, setTimeout(0) — эталон js:136). */
  useEffect(() => {
    if (!menu) return setOn(false);
    const raf = requestAnimationFrame(() => setOn(true));
    const down = (e: PointerEvent) => {
      if (!(e.target instanceof Node && menuRef.current?.contains(e.target))) setMenu(null);
    };
    const t = window.setTimeout(() => document.addEventListener("pointerdown", down, true), 0);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
      document.removeEventListener("pointerdown", down, true);
    };
  }, [menu]);

  useEffect(() => () => window.clearTimeout(lpT.current), []);

  const menuPos = (): { left: number; top: number } => {
    if (!menu) return { left: 0, top: 0 };
    const w = 168, h = 28; // оценка до маунта; клампы эталона showOpMenu (js:140-142)
    const left = Math.max(6, Math.min(menu.anchor.left, window.innerWidth - w - 6));
    let top = menu.anchor.top - h - 6;
    if (top < 6) top = menu.anchor.bottom + 6;
    return { left, top };
  };

  return (
    <div className={`${cfg.cell} ${css.cellInline}`}>
      <span className={cfg.klbl}>Операторы</span>
      <div className={css.ops}>
        {ops.map((g, i) => (
          <button
            key={i}
            type="button"
            className={`${css.opq} ${G_CLS[g]}`}
            title={G_TITLE[g]}
            onPointerDown={(e) => {
              longRef.current = false;
              const r = e.currentTarget.getBoundingClientRect();
              lpT.current = window.setTimeout(() => {
                longRef.current = true;
                setMenu({ i, anchor: r });
              }, LP_MS);
            }}
            onPointerUp={() => window.clearTimeout(lpT.current)}
            onPointerLeave={() => window.clearTimeout(lpT.current)}
            onPointerCancel={() => window.clearTimeout(lpT.current)}
            onContextMenu={(e) => e.preventDefault()}
            onClick={() => {
              if (longRef.current) { longRef.current = false; return; }
              const next = [...ops];
              next[i] = g === "m" ? "f" : g === "f" ? "a" : "m";
              onChange(next);
            }}
          >
            <OpSvg g={g} w={16} />
          </button>
        ))}
        <button
          type="button"
          className={`${css.opq} ${css.oAdd}`}
          title="Добавить оператора"
          onClick={() => onChange([...ops, "a"])}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      </div>
      {menu
        ? createPortal(
            <div ref={menuRef} className={`${css.opmenu} ${on ? css.on : ""}`} style={menuPos()}>
              <button
                type="button"
                onClick={() => {
                  onChange(ops.filter((_, x) => x !== menu.i));
                  setMenu(null);
                }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 5l14 14M19 5L5 19" />
                </svg>
                Удалить оператора
              </button>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
